import { CalendarClock } from "lucide-react";
import { computeAvailability, availabilityLabel, availabilityTone } from "@/lib/availability";

const TONE_CLASS = {
    now:     "border-emerald-300 bg-emerald-50 text-emerald-800",
    soon:    "border-emerald-300 bg-emerald-50 text-emerald-800",
    ok:      "border-amber-300 bg-amber-50 text-amber-800",
    far:     "border-border bg-secondary text-foreground/70",
    neutral: "border-dashed border-border bg-white text-muted-foreground",
};

/**
 * AvailabilityPill — small status chip showing when the candidate is available.
 * Pure UI; pulls data from candidate.screening_profile + optional application.
 * Renders nothing when there's no signal AND `hideWhenUnknown` is true (kept
 * default so cards stay clean).
 */
export default function AvailabilityPill({ candidate, application, size = "sm", hideWhenUnknown = true, testIdPrefix = "avail" }) {
    const a = computeAvailability(candidate, application);
    if (!a.date && hideWhenUnknown) return null;
    const tone = availabilityTone(a.daysOut);
    const label = a.date ? availabilityLabel(a) : "Availability unknown";
    const isLg = size === "md";
    const dim = isLg ? "text-xs px-2 py-1" : "text-[10px] px-1.5 py-0.5";
    const tip = a.source === "notice_period"
        ? "From notice period"
        : a.source === "earliest_join_date"
            ? "From candidate's earliest join date"
            : a.source === "application_target"
                ? "From application's target join date"
                : "No availability signal captured";
    return (
        <span
            className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest border ${dim} ${TONE_CLASS[tone] || TONE_CLASS.neutral}`}
            title={tip}
            data-testid={`${testIdPrefix}-${candidate?.id || "unknown"}`}
            data-availability-days={a.daysOut ?? ""}
        >
            <CalendarClock className="w-3 h-3" />
            {label}
        </span>
    );
}
